import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import { Underline } from '../extensions/Underline';
import { Indent } from '../extensions/Indent';
import { SermonSection } from '../extensions/SermonSections';
import { SlashCommands } from '../extensions/SlashCommands';
import { FormatBar } from './FormatBar';
import { SaveNotification } from './SaveNotification';
import { useSermon } from '../hooks/useSermon';
import { useSermons } from '../hooks/useSermons';
import { TextEnhancer } from './TextEnhancer';
import { SermonShareModal } from './SermonShareModal';
import SlideEditor from './SlideEditor';
import { TextSelectionMenu } from './TextSelectionMenu';
import { CommentaryViewer } from './CommentaryViewer';
import { CommentaryLibrary } from './CommentaryLibrary';
import mammoth from 'mammoth';
import {
  Loader2, Save, Share2, Presentation, AlertCircle, ChevronDown, ChevronUp, ChevronLeft,
  Calendar, BookOpen, CheckCircle, Clock, ArrowLeft, Paperclip,
  Brain, Eye, X, FileText, MessageSquare, Music, Target, FileUp, Menu, Languages, Download, Wrench, PenLine,
  Image as ImageIcon, Trash2, BookmarkCheck, PanelRight, Sparkles, Heart, Check, Book, Compass
} from 'lucide-react';
import { TheologicalAssistant } from './TheologicalAssistant';
import { PassageGuide } from './Tools/PassageGuide';
import { format, parseISO } from 'date-fns';
import Sortable from 'sortablejs';
import { v4 as uuidv4 } from 'uuid';
import { analyzeSermon, analyzeLanguage } from '../lib/openai';
import type { LanguageAnalysisJSON } from '../lib/openai';
import { useLanguageAnalysis } from '../hooks/useLanguageAnalysis';
import { createSermonPDF } from '../lib/pdf';
import { createWordDocument } from '../lib/docx';
import { parseSermonContent, sanitizeFilename } from '../lib/richText';
import { EditorState } from '@tiptap/pm/state';
import { supabase } from '../lib/supabase';
import { useEditorPresence } from '../hooks/useSermonPresence';
import { useSermonMedia } from '../hooks/useSermonMedia';

// Editor component for creating and editing sermons
export function Editor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [scripture, setScripture] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const [status, setStatus] = useState<'draft' | 'complete'>('draft');
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [saveNotification, setSaveNotification] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' | 'info' });
  const [selectedText, setSelectedText] = useState('');
  const [showTextEnhancer, setShowTextEnhancer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSlideEditor, setShowSlideEditor] = useState(false);
  const [showSeriesDropdown, setShowSeriesDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  type PanelTab = 'commentary' | 'tools' | 'generate' | 'assistant' | 'guide';
  const [activePanelTab, setActivePanelTab] = useState<PanelTab | null>(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showCreateSeriesModal, setShowCreateSeriesModal] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [newSeriesDescription, setNewSeriesDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [fullAnalysisResult, setFullAnalysisResult] = useState<any>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [langAnalyzing, setLangAnalyzing] = useState(false);
  const [fullLangResult, setFullLangResult] = useState<LanguageAnalysisJSON | null>(null);
  const [langError, setLangError] = useState<string | null>(null);
  const [langInsertedId, setLangInsertedId] = useState<number | null>(null);
  const [langSaved, setLangSaved] = useState(false);
  const { saveAnalysis: saveLangAnalysis } = useLanguageAnalysis();
  const [isMetadataCollapsed, setIsMetadataCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [importError, setImportError] = useState<string | null>(null);
  const [importingFile, setImportingFile] = useState(false);
  const [showMetadataPanel, setShowMetadataPanel] = useState(true);
  const [showCommentary, setShowCommentary] = useState(false);
  const [commentaryReference, setCommentaryReference] = useState('');
  const [clarityMode, setClarityMode] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const seriesDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const { series, createSeries } = useSermons();
  const {
    saving,
    error,
    lastSaved,
    currentVersion,
    autosaveConfig,
    isOffline,
    offlineMode,
    userPermission,
    saveSermon,
    loadSermon,
    triggerAutosave,
    saveBeforeExit,
    setContent: setEditorContent
  } = useSermon(id);

  const isOwner = !id || userPermission === 'owner' || userPermission === null;
  const isEditor = userPermission === 'editor';
  const isViewer = userPermission === 'viewer';
  const canEdit = isOwner || isEditor;

  // Broadcast editing presence to team members viewing the sermon list
  useEditorPresence(id, canEdit);

  const { media: sermonMedia, getPublicUrl: getMediaPublicUrl, deleteFile: deleteMediaFile } = useSermonMedia(id);
  const savedGraphics = sermonMedia.filter(m => m.section_id === 'ai-generated');

  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: 'Start writing your sermon... (Press / to insert sermon sections)' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Typography,
      Underline,
      Indent,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'link-style',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      SermonSection,
      SlashCommands
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none'
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          if (event.key === 'Enter' && event.shiftKey) {
            return true;
          }
          return false;
        },
        // Grammarly fires beforeinput with insertReplacementText when accepting a correction.
        // ProseMirror doesn't handle this inputType — it DOM-diffs the mutation and garbles
        // surrounding content. We intercept it and apply the replacement as a proper transaction.
        beforeinput: (view, event) => {
          const inputEvent = event as InputEvent;
          if (inputEvent.inputType !== 'insertReplacementText') return false;
          const ranges = Array.from(inputEvent.getTargetRanges?.() ?? []);
          if (!ranges.length) return false;
          const replacementText = inputEvent.dataTransfer?.getData('text/plain') ?? '';
          const range = ranges[0];
          try {
            const from = view.posAtDOM(range.startContainer, range.startOffset);
            const to = view.posAtDOM(range.endContainer, range.endOffset);
            if (from < 0 || to < 0 || from > to) return false;
            view.dispatch(view.state.tr.insertText(replacementText, from, to));
            event.preventDefault();
            return true;
          } catch {
            return false;
          }
        }
      }
    },
    onCreate: ({ editor }) => {
      if (id) editor.storage.sermonId = id;
    },
    onUpdate: ({ editor }) => setEditorContent(editor.getHTML()),
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      setSelectedText(from !== to ? editor.state.doc.textBetween(from, to, ' ') : '');
    }
  });

  // Store sermon ID in editor storage
  useEffect(() => {
    if (editor && id) editor.storage.sermonId = id;
  }, [editor, id]);

  // Load sermon data
  useEffect(() => {
    const fetchSermon = async () => {
      if (id) {
        console.log('Loading sermon with ID:', id);
        const sermon = await loadSermon(id);
        console.log('Loaded sermon:', sermon);
        if (sermon) {
          setTitle(sermon.title);
          setScripture(sermon.scripture);
          setDate(sermon.date);
          setStatus(sermon.status);
          setSeriesId(sermon.series_id);
          setYoutubeUrl(sermon.youtube_url || '');
          if (editor) {
            console.log('Setting editor content:', sermon.content);
            // Set content without triggering onUpdate
            editor.commands.setContent(sermon.content || '', { emitUpdate: false });
            
            // Clear undo history by creating a fresh editor state with the current document
            // This prevents Ctrl+Z from erasing the loaded sermon content
            setTimeout(() => {
              if (editor && !editor.isDestroyed) {
                const currentDoc = editor.state.doc;
                const currentSchema = editor.state.schema;
                const currentPlugins = editor.state.plugins;
                
                // Create a completely new state with the same document but fresh history
                const newState = EditorState.create({
                  doc: currentDoc,
                  schema: currentSchema,
                  plugins: currentPlugins
                });
                
                editor.view.updateState(newState);
              }
            }, 50);
          }
        } else {
          console.error('Failed to load sermon');
        }
      }
    };
    fetchSermon();
  }, [id, loadSermon, editor]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(event.target as Node)) {
        setShowSeriesDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set up sortable for drag and drop
  useEffect(() => {
    if (!editorRef.current || !editor) return;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const sortableInstance = Sortable.create(editorRef.current, {
      handle: '[data-drag-handle]',
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      forceFallback: isTouchDevice, // iOS doesn't support HTML5 drag API; desktop should use native drag
      fallbackTolerance: isTouchDevice ? 5 : 0,
      onStart: () => document.body.classList.add('dragging-active'),
      onEnd: (event) => {
        document.body.classList.remove('dragging-active');
        if (event.oldIndex !== event.newIndex && event.oldIndex !== undefined && event.newIndex !== undefined) {
          const positions: number[] = [];
          editor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'sermonSection') positions.push(pos);
            return true;
          });
          if (positions[event.oldIndex] !== undefined && positions[event.newIndex] !== undefined) {
            editor.commands.moveSermonSection(positions[event.oldIndex], positions[event.newIndex]);
          }
        }
      }
    });
    return () => sortableInstance.destroy();
  }, [editor]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!editor) return;
    const content = editor.getHTML();
    const updatedContent = addSectionIds(content);
    if (updatedContent !== content) editor.commands.setContent(updatedContent, { emitUpdate: false });
    const sermon = await saveSermon({
      title,
      scripture,
      content: updatedContent,
      date,
      status,
      series_id: seriesId,
      youtube_url: youtubeUrl || null
    });
    setSaveNotification({
      show: true,
      message: sermon ? 'Sermon saved successfully' : error || 'Failed to save sermon',
      type: sermon ? 'success' : 'error'
    });
    if (!id && sermon?.id) navigate(`/dashboard/editor/${sermon.id}`, { replace: true });
  }, [editor, id, title, scripture, date, status, seriesId, youtubeUrl, saveSermon, error, navigate]);

  // Add unique IDs to sermon sections
  const addSectionIds = (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    let modified = false;
    doc.querySelectorAll('.sermon-section').forEach(section => {
      if (!section.hasAttribute('data-section-id')) {
        section.setAttribute('data-section-id', uuidv4());
        modified = true;
      }
    });
    return modified ? doc.body.innerHTML : content;
  };

  // Handle creating a new series
  const handleCreateSeries = useCallback(() => setShowCreateSeriesModal(true), []);

  const handleCreateSeriesSubmit = useCallback(async () => {
    if (!newSeriesName.trim()) return;
    const newSeries = await createSeries({
      title: newSeriesName.trim(),
      description: newSeriesDescription.trim()
    });
    if (newSeries) {
      setSeriesId(newSeries.id);
      setShowSeriesDropdown(false);
      setShowCreateSeriesModal(false);
      setNewSeriesName('');
      setNewSeriesDescription('');
    }
  }, [newSeriesName, newSeriesDescription, createSeries]);

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'MMMM d, yyyy');
    } catch {
      return '';
    }
  };

  // Apply text enhancement
  const handleApplyEnhancement = useCallback((enhancedText: string) => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    editor.chain().focus().deleteRange({ from, to }).insertContent(enhancedText).run();
    setShowTextEnhancer(false);
  }, [editor]);

  // Handle analyze sermon
  const handleExportForSermonScribe = useCallback(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const lines: string[] = [];
    if (title) lines.push(title);
    if (scripture) lines.push(scripture);
    if (date) lines.push(format(parseISO(date), 'MMMM d, yyyy'));
    if (lines.length > 0) lines.push('', '---', '');
    const walkNodes = (node: ChildNode) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }
      const el = node as HTMLElement;
      const tag = el.tagName?.toLowerCase();
      let text = '';
      el.childNodes.forEach(child => { text += walkNodes(child); });
      if (['p', 'div', 'h1', 'h2', 'h3', 'li', 'blockquote'].includes(tag)) {
        return text.trim() + '\n\n';
      }
      if (tag === 'br') return '\n';
      return text;
    };
    let bodyText = '';
    tempDiv.childNodes.forEach(child => { bodyText += walkNodes(child); });
    bodyText = bodyText.replace(/\n{3,}/g, '\n\n').trim();
    lines.push(bodyText);
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'sermon').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [editor, title, scripture, date]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = useCallback(() => {
    if (!editor) return;
    const elements = parseSermonContent(editor.getHTML());
    const blob = createSermonPDF({ title, scripture, date }, elements);
    downloadBlob(blob, `${sanitizeFilename(title)}.pdf`);
  }, [editor, title, scripture, date]);

  const handleExportWord = useCallback(async () => {
    if (!editor) return;
    const elements = parseSermonContent(editor.getHTML());
    const blob = await createWordDocument({ title, scripture, date }, elements);
    downloadBlob(blob, `${sanitizeFilename(title)}.docx`);
  }, [editor, title, scripture, date]);

  const handleAnalyzeSermon = useCallback(async () => {
    if (!editor) return;
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const content = editor.getHTML();
      const result = await analyzeSermon(content, scripture || undefined);
      if (result.success) {
        const c = result.content;
        setFullAnalysisResult(c);
        if (typeof c === 'string') {
          setAnalysisResult(c);
        } else if (c && typeof c === 'object' && 'overallAssessment' in c) {
          setAnalysisResult((c as any).overallAssessment?.summary ?? 'Analysis complete. View full results in Research.');
        } else {
          setAnalysisResult('Analysis complete. View full results in Research.');
        }
        setActivePanelTab('tools');
      } else {
        setAnalysisError(result.error || 'Failed to analyze sermon');
      }
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : 'An error occurred during analysis');
    } finally {
      setAnalyzing(false);
    }
  }, [editor, scripture]);

  const handleLanguageAnalysis = useCallback(async () => {
    if (!editor) return;
    setLangAnalyzing(true);
    setLangError(null);
    try {
      const content = editor.getHTML();
      const result = await analyzeLanguage(content, scripture || undefined);
      if (result.success) {
        setFullLangResult(result.content as LanguageAnalysisJSON);
        setLangSaved(false);
        setActivePanelTab('tools');
      } else {
        setLangError(result.error || 'Failed to run language analysis');
      }
    } catch (err) {
      setLangError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLangAnalyzing(false);
    }
  }, [editor, scripture]);

  const handleSaveLangResult = useCallback(async () => {
    if (!fullLangResult || langSaved) return;
    try {
      await saveLangAnalysis({
        sermon_id: id || null,
        title: title || fullLangResult.scripture || 'Word Study',
        content: fullLangResult,
      });
      setLangSaved(true);
    } catch (err) {
      setLangError(err instanceof Error ? err.message : 'Failed to save word study');
    }
  }, [fullLangResult, langSaved, saveLangAnalysis, id, title]);

  const insertLanguageTerm = useCallback((term: LanguageAnalysisJSON['terms'][0], index: number) => {
    if (!editor) return;
    const html = [
      `<p><strong>${term.word} (${term.transliteration}) — ${term.strongsNumber} · ${term.language} ${term.partOfSpeech}</strong></p>`,
      `<p><em>${term.literalMeaning}</em></p>`,
      `<p>${term.preachingApplication}</p>`,
    ].join('');
    editor.chain().focus().insertContent(html).run();
    setLangInsertedId(index);
    setTimeout(() => setLangInsertedId(null), 2000);
  }, [editor]);

  // Handle preach mode
  const handlePreachMode = useCallback(async () => {
    await saveBeforeExit();
    if (id) {
      navigate(`/dashboard/preach/${id}`);
    } else {
      const sermon = await saveSermon({
        title,
        scripture,
        content: editor?.getHTML() || '',
        date,
        status,
        series_id: seriesId,
        youtube_url: youtubeUrl || null
      });
      if (sermon?.id) navigate(`/dashboard/preach/${sermon.id}`);
    }
  }, [editor, id, title, scripture, date, status, seriesId, youtubeUrl, saveSermon, saveBeforeExit, navigate]);

  // Helper function to validate if content should be scripture
  const isLikelyScripture = useCallback((text: string): boolean => {
    const hasBookReference = /\b(Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Joshua|Judges|Ruth|1\s*Samuel|2\s*Samuel|1\s*Kings|2\s*Kings|1\s*Chronicles|2\s*Chronicles|Ezra|Nehemiah|Esther|Job|Psalms?|Proverbs?|Ecclesiastes|Song\s*of\s*Songs?|Isaiah|Jeremiah|Lamentations|Ezekiel|Daniel|Hosea|Joel|Amos|Obadiah|Jonah|Micah|Nahum|Habakkuk|Zephaniah|Haggai|Zechariah|Malachi|Matthew|Mark|Luke|John|Acts|Romans|1\s*Corinthians|2\s*Corinthians|Galatians|Ephesians|Philippians|Colossians|1\s*Thessalonians|2\s*Thessalonians|1\s*Timothy|2\s*Timothy|Titus|Philemon|Hebrews|James|1\s*Peter|2\s*Peter|1\s*John|2\s*John|3\s*John|Jude|Revelation)\s+\d+/i.test(text);

    const hasVerseNumbers = /\b\d+:\d+/g.test(text);
    const verseMatches = text.match(/\b\d+:\d+/g);
    const hasMultipleVerses = verseMatches && verseMatches.length > 1;

    const hasScriptureIndicators = /\b(scripture|verse|passage|text|reads?|says?|word of god|lord|god)\b/i.test(text);
    const hasQuotes = text.includes('"') && text.length > 30;

    // Strong indicators of scripture
    if (hasBookReference) return true;
    if (hasVerseNumbers && hasScriptureIndicators) return true;
    if (hasMultipleVerses && hasQuotes) return true;

    // Exclude common false positives
    const hasFalsePositives = /\b(israel|war|iran|news|week|today|america|politics|church|people|pastor|sermon|announce|picnic|baptism|memorial|service)\b/i.test(text);
    if (hasFalsePositives && !hasBookReference) return false;

    return false;
  }, []);

  // Function to parse content and create sermon sections
  const parseContentIntoSections = useCallback((content: string) => {
    if (!editor) {
      console.error('Editor not available for content parsing');
      return;
    }

    console.log('Starting parseContentIntoSections with content length:', content.length);

    try {
      // Clean up the content first - more aggressive cleaning
      let cleanContent = content
        .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
        .replace(/\r\n/g, '\n') // Normalize line endings
        .replace(/\r/g, '\n')
        .replace(/\u2013/g, '-') // Replace en dash
        .replace(/\u2014/g, '--') // Replace em dash
        .replace(/\u2018/g, "'") // Replace left single quote
        .replace(/\u2019/g, "'") // Replace right single quote
        .replace(/\u201C/g, '"') // Replace left double quote
        .replace(/\u201D/g, '"') // Replace right double quote
        .replace(/\u2026/g, '...') // Replace ellipsis
        .trim();

      if (!cleanContent) {
        console.error('No content to parse after cleaning');
        return;
      }

      // STRICT label patterns - MUST have a delimiter (colon, dash, or period) after the label
      // Only match very explicit labels, not generic words
      const labelPatterns: { pattern: RegExp; type: string }[] = [
        // Scripture patterns - require delimiter
        { pattern: /^scripture\s*[:.\-]\s*/i, type: 'scripture' },
        { pattern: /^bible\s*(?:text|reading|verse|passage)\s*[:.\-]\s*/i, type: 'scripture' },
        // Main point patterns - require delimiter or numbering
        { pattern: /^main\s*point\s*(?:#?\d+)?\s*[:.\-]\s*/i, type: 'main-point' },
        { pattern: /^key\s*point\s*(?:#?\d+)?\s*[:.\-]\s*/i, type: 'main-point' },
        { pattern: /^point\s*(?:#?\d+|one|two|three|four|five)\s*[:.\-]\s*/i, type: 'main-point' },
        { pattern: /^big\s*idea\s*[:.\-]\s*/i, type: 'main-point' },
        // Quote patterns - require delimiter
        { pattern: /^quote\s*[:.\-]\s*/i, type: 'quote' },
        { pattern: /^quotation\s*[:.\-]\s*/i, type: 'quote' },
        // Illustration patterns - require delimiter
        { pattern: /^illustration\s*[:.\-]\s*/i, type: 'illustration' },
        // Application patterns - require delimiter
        { pattern: /^application\s*[:.\-]\s*/i, type: 'application' },
        { pattern: /^takeaway\s*[:.\-]\s*/i, type: 'application' },
        { pattern: /^action\s*(?:point|step)\s*[:.\-]\s*/i, type: 'application' },
        // Opening patterns - require delimiter
        { pattern: /^(?:opening|introduction)\s*[:.\-]\s*/i, type: 'opening' },
        // Closing patterns - require delimiter
        { pattern: /^(?:closing|conclusion)\s*[:.\-]\s*/i, type: 'closing' },
      ];

      // Helper function to check for explicit labels at the start of text
      // STRICT: Only match if there's a clear delimiter and content after it
      const detectExplicitLabel = (text: string): { type: string | null; contentAfterLabel: string | null; confidence: number } => {
        const trimmedText = text.trim();
        const firstLine = trimmedText.split('\n')[0].trim();
        
        // Only check the first line for labels (max 60 chars to avoid matching sentences)
        if (firstLine.length > 60) {
          return { type: null, contentAfterLabel: null, confidence: 0 };
        }
        
        // Check each label pattern against the first line
        for (const { pattern, type } of labelPatterns) {
          const match = firstLine.match(pattern);
          if (match) {
            const labelLength = match[0].length;
            const contentOnFirstLine = firstLine.slice(labelLength).trim();
            const contentOnOtherLines = trimmedText.slice(firstLine.length).trim();
            
            // Must have content either on same line or following lines
            const fullContent = contentOnFirstLine + (contentOnOtherLines ? '\n' + contentOnOtherLines : '');
            
            if (fullContent.length > 0) {
              console.log(`Detected explicit label: "${match[0].trim()}" -> ${type}`);
              return { 
                type, 
                contentAfterLabel: fullContent.trim(),
                confidence: 1.0
              };
            }
          }
        }
        
        return { type: null, contentAfterLabel: null, confidence: 0 };
      };

      // Helper function to analyze if content should be a sermon section (fallback heuristics)
      const analyzeContentHeuristics = (text: string, paragraphIndex: number, totalParagraphs: number): { type: string | null, confidence: number } => {
        const lowerText = text.toLowerCase();

        // Score for each section type
        let scriptureScore = 0;
        let illustrationScore = 0;
        let quoteScore = 0;
        let applicationScore = 0;
        let mainPointScore = 0;
        let openingScore = 0;
        let closingScore = 0;

        // Scripture indicators
        if (isLikelyScripture(text)) scriptureScore += 1.0;
        if (/\b(scripture|verse|passage|text|reads?|says?|word of god|lord|god)\b/i.test(text)) scriptureScore += 0.3;
        if (text.includes('"') && text.length > 30) scriptureScore += 0.2;

        // Illustration indicators
        if (/\b(illustration|story|example|imagine|picture this|consider|think about|once upon|there was|there once|a man|a woman|a person|a family)\b/i.test(text)) illustrationScore += 0.8;
        if (/\b(analogy|metaphor|simile|parable)\b/i.test(text)) illustrationScore += 0.5;

        // Quote indicators
        if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith('"') && text.endsWith('"'))) {
          if (text.includes('—') || text.includes('–')) quoteScore += 1.0;
          else quoteScore += 0.7;
        }
        if (/\b(quote|said|stated|declared|wrote|according to|as [a-zA-Z]+ says)\b/i.test(text)) quoteScore += 0.5;

        // Application indicators
        if (/\b(application|apply|practice|action|implement|how to|what does this mean|so what|now what|takeaway)\b/i.test(text)) applicationScore += 1.0;
        if (/\b(we must|we should|we need to|you must|you should|you need to|let us)\b/i.test(text)) applicationScore += 0.6;

        // Main point indicators
        if (/^\s*(\d+[\.\)]\s|[IVX]+[\.\)]\s|[A-Z][\.\)]\s)/i.test(text)) mainPointScore += 0.9;
        if (/\b(main point|key point|first|second|third|fourth|fifth|finally|point\s*\d+|number\s*\d+)\b/i.test(text)) mainPointScore += 0.7;

        // Opening indicators (early paragraphs)
        if (paragraphIndex < 3 && /\b(introduction|opening|welcome|good morning|good evening|today|this morning|this evening|hello)\b/i.test(text)) openingScore += 0.8;

        // Closing indicators (late paragraphs)
        if (paragraphIndex > totalParagraphs - 3 && /\b(conclusion|closing|in conclusion|to conclude|finally|in closing|let us pray|amen|god bless)\b/i.test(text)) closingScore += 0.8;

        // Determine the best fit
        const scores = [
          { type: 'scripture', score: scriptureScore },
          { type: 'illustration', score: illustrationScore },
          { type: 'quote', score: quoteScore },
          { type: 'application', score: applicationScore },
          { type: 'main-point', score: mainPointScore },
          { type: 'opening', score: openingScore },
          { type: 'closing', score: closingScore },
        ];

        // Sort by score descending
        scores.sort((a, b) => b.score - a.score);

        // Return the highest scoring type if confidence is above a threshold
        if (scores[0].score > 0.6) { // Lowered threshold slightly for better detection
          return { type: scores[0].type, confidence: scores[0].score };
        } else {
          return { type: null, confidence: scores[0].score };
        }
      };

      // STRICT MODE: Only create sermon sections for explicit labels
      // No heuristic-based detection - content without clear labels stays as plain text
      const shouldBeSermonSection = (text: string, paragraphIndex: number, totalParagraphs: number): { type: string | null, confidence: number, contentToUse: string } => {
        // Only check for explicit labels - no heuristic fallback
        const labelResult = detectExplicitLabel(text);
        if (labelResult.type) {
          return {
            type: labelResult.type,
            confidence: labelResult.confidence,
            contentToUse: labelResult.contentAfterLabel || text
          };
        }
        
        // No label found - return as plain text (no section created)
        return {
          type: null,
          confidence: 0,
          contentToUse: text
        };
      };

      // Valid sermon section types
      const validSectionTypes = ['scripture', 'illustration', 'quote', 'application', 'main-point', 'opening', 'closing'];

      // Split content into logical sections - look for clear paragraph breaks
      const rawParagraphs = cleanContent.split(/\n\s*\n/).filter(p => p.trim());
      console.log(`Processing ${rawParagraphs.length} raw paragraphs`);

      // Group related paragraphs together to prevent sentence fragmentation
      const paragraphs = [];
      let currentGroup = '';
      
      for (let i = 0; i < rawParagraphs.length; i++) {
        const para = rawParagraphs[i].trim();
        if (!para) continue;
        
        // Check if this paragraph should start a new group
        const isNewSection = (
          // Clear section indicators
          /^\s*(scripture|illustration|quote|application|main\s*point|opening|closing|introduction|conclusion)/i.test(para) ||
          // Numbered or lettered lists
          /^\s*(\d+[\.\)]\s|[IVX]+[\.\)]\s|[A-Z][\.\)]\s)/i.test(para) ||
          // Headers or titles (all caps, ends with colon, etc.)
          (para.endsWith(':') && para.length < 80 && !para.includes('.')) ||
          (para === para.toUpperCase() && para.length > 5 && para.length < 60) ||
          // Long paragraphs that are clearly complete thoughts
          (para.length > 200 && /[.!?]\s*$/.test(para)) ||
          // First paragraph (always start new group)
          i === 0
        );
        
        // Check if previous group should end here
        const shouldEndPrevious = (
          currentGroup.length > 500 || // Prevent overly long groups
          (currentGroup.length > 0 && isNewSection)
        );
        
        if (shouldEndPrevious && currentGroup) {
          paragraphs.push(currentGroup.trim());
          currentGroup = para;
        } else {
          if (currentGroup) {
            // Add space between paragraphs in the same group
            currentGroup += '\n\n' + para;
          } else {
            currentGroup = para;
          }
        }
      }
      
      // Add the final group
      if (currentGroup) {
        paragraphs.push(currentGroup.trim());
      }
      
      console.log(`Grouped into ${paragraphs.length} logical sections`);

      // Build all content first, then insert in one operation
      const contentParts: string[] = [];

      for (let index = 0; index < paragraphs.length; index++) {
        const paragraph = paragraphs[index];
        const trimmedParagraph = paragraph.trim();
        if (!trimmedParagraph) continue;

        try {
          // Check if this paragraph should be a sermon section
          const sectionAnalysis = shouldBeSermonSection(trimmedParagraph, index, paragraphs.length);

          // Only create a sermon section if we have high confidence and it's a valid type
          if (sectionAnalysis.confidence >= 0.7 &&
              sectionAnalysis.type &&
              validSectionTypes.includes(sectionAnalysis.type)) {

            console.log(`Creating ${sectionAnalysis.type} section with confidence ${sectionAnalysis.confidence}`);

            const sectionId = crypto.randomUUID();
            const sectionTitle = sectionAnalysis.type.split('-').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');

            // Use contentToUse which has the label stripped if detected
            const contentForSection = sectionAnalysis.contentToUse || trimmedParagraph;
            
            // Clean the content for the section - escape any problematic characters
            const sectionContent = contentForSection
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;')
              .replace(/\n/g, '<br>');

            const sectionHtml = `<div class="sermon-section sermon-section-${sectionAnalysis.type}" data-type="${sectionAnalysis.type}" data-section-id="${sectionId}"><h3>${sectionTitle}</h3><p>${sectionContent}</p></div>`;

            contentParts.push(sectionHtml);
          } else {
            // Default to regular text for uncertain content
            console.log(`Adding as regular text (confidence: ${sectionAnalysis.confidence}, type: ${sectionAnalysis.type || 'N/A'})`);

            // Clean the content for regular paragraph - escape any problematic characters
            const regularContent = trimmedParagraph
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;')
              .replace(/\n/g, '<br>');

            contentParts.push(`<p>${regularContent}</p>`);
          }

          // Add minimal spacing between content blocks (only if it's actually a section)
          if (sectionAnalysis.confidence >= 0.7 && sectionAnalysis.type && validSectionTypes.includes(sectionAnalysis.type)) {
            contentParts.push('<p></p>');
          }

        } catch (paragraphError) {
          console.error(`Error processing paragraph ${index}:`, paragraphError);
          // Still try to add as plain text if section creation fails
          try {
            const fallbackContent = trimmedParagraph
              .replace(/[<>&"']/g, '') // Remove problematic characters
              .replace(/\n/g, ' ');
            contentParts.push(`<p>${fallbackContent}</p>`);
          } catch (fallbackError) {
            console.error(`Fallback insertion also failed for paragraph ${index}:`, fallbackError);
          }
        }
      }

      // Now set all content at once to avoid nesting issues
      const finalContent = contentParts.join('');
      editor.commands.setContent(finalContent);

      console.log('Finished inserting all sections');

      // Trigger autosave after import
      setTimeout(() => {
        triggerAutosave();
      }, 1000);

      setSaveNotification({
        show: true,
        message: `Successfully processed ${paragraphs.length} sections from Word document`,
        type: 'success'
      });

    } catch (overallError) {
      console.error('Overall error during content parsing:', overallError);
      setSaveNotification({
        show: true,
        message: `Error processing Word document: ${overallError instanceof Error ? overallError.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  }, [editor, triggerAutosave, setSaveNotification]);

  // Handle import Word document
  const handleImportWord = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.docx, .doc';
    fileInput.style.display = 'none';
    fileInput.addEventListener('change', async (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setSaveNotification({
          show: true,
          message: `Reading ${file.name}...`,
          type: 'info'
        });

        try {
          setImportError(null);
          setImportingFile(true);

          if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
            if (!editor) {
              throw new Error('Editor not initialized');
            }

            const arrayBuffer = await file.arrayBuffer();
            let extractedText = '';

            try {
              // Try raw text extraction first to avoid XML parsing issues
              try {
                const textResult = await mammoth.extractRawText({
                  arrayBuffer,
                  options: {
                    ignoreEmptyParagraphs: false
                  }
                });
                extractedText = textResult.value;
                console.log('Raw text extraction successful');
              } catch (textError) {
                console.warn('Raw text extraction failed, trying HTML extraction:', textError);

                // Fallback to HTML extraction with better error handling
                const htmlResult = await mammoth.convertToHtml({
                  arrayBuffer,
                  options: {
                    ignoreEmptyParagraphs: false,
                    styleMap: [
                      "p[style-name='Heading 1'] => h1:fresh",
                      "p[style-name='Heading 2'] => h2:fresh",
                      "p[style-name='Heading 3'] => h3:fresh"
                    ]
                  }
                });

                // More aggressive HTML cleaning to prevent XML parsing errors
                let cleanHtml = htmlResult.value
                  .replace(/&nbsp;/g, ' ')
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'")
                  .replace(/&#8217;/g, "'")
                  .replace(/&#8216;/g, "'")
                  .replace(/&#8220;/g, '"')
                  .replace(/&#8221;/g, '"')
                  .replace(/&#8211;/g, '-')
                  .replace(/&#8212;/g, '--')
                  .replace(/&#8230;/g, '...')
                  .replace(/&[a-zA-Z0-9#]+;/g, ' '); // Remove any remaining HTML entities

                extractedText = new DOMParser().parseFromString(cleanHtml, 'text/html').body.textContent ?? '';
                console.log('HTML extraction successful as fallback');
              }

            } catch (extractionError) {
              console.error('Both extraction methods failed:', extractionError);
              throw new Error('Unable to process this Word document. The document may be corrupted, password-protected, or in an unsupported format.');
            }

            // Clean up extracted text
            extractedText = extractedText
              .replace(/\u00A0/g, ' ')
              .replace(/\r\n/g, '\n')
              .replace(/\r/g, '\n')
              .trim();

            if (!extractedText) {
              throw new Error('No text content found in the document');
            }

            console.log('Extracted text length:', extractedText.length);
            console.log('First 500 characters:', extractedText.substring(0, 500));

            setSaveNotification({
              show: true,
              message: 'Analyzing and polishing document with AI — this may take 15-30 seconds...',
              type: 'info'
            });

            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) {
                throw new Error('Please sign in to import documents');
              }

              const aiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`;
              console.log('Calling AI import at URL:', aiUrl);
              console.log('Content length being sent:', extractedText.length);

              const response = await fetch(aiUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                },
                body: JSON.stringify({
                  type: 'sermonImport',
                  content: extractedText
                })
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error('AI parsing failed with status:', response.status, errorText);
                throw new Error(`AI parsing failed: ${response.status}`);
              }

              const aiResult = await response.json();
              console.log('AI response:', JSON.stringify(aiResult).substring(0, 500));
              
              if (!aiResult.success || !aiResult.content) {
                console.error('Invalid AI response structure:', aiResult);
                throw new Error('Invalid AI response');
              }

              let parsedContent;
              try {
                parsedContent = JSON.parse(aiResult.content);
              } catch (parseError) {
                console.error('Failed to parse AI content as JSON:', aiResult.content?.substring(0, 500));
                throw new Error('Failed to parse AI response');
              }
              
              if (!parsedContent.sections || !Array.isArray(parsedContent.sections)) {
                console.error('Invalid parsed structure:', parsedContent);
                throw new Error('Invalid parsed structure');
              }

              console.log('AI parsed sections:', parsedContent.sections.length);

              // Build HTML content from AI-parsed sections
              const validSectionTypes = ['scripture', 'illustration', 'quote', 'application', 'main-point', 'opening', 'closing'];
              const contentParts: string[] = [];

              const buildContentHtml = (rawContent: string): string => {
                const lines = rawContent.split('\n');
                let html = '';
                let inList = false;
                for (const line of lines) {
                  const trimmed = line.trim();
                  if (!trimmed) {
                    if (inList) { html += '</ul>'; inList = false; }
                    continue;
                  }
                  const escaped = trimmed
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;');
                  if (/^[•\-\*]\s/.test(trimmed)) {
                    if (!inList) { html += '<ul>'; inList = true; }
                    html += `<li>${escaped.replace(/^[•\-\*]\s*/, '')}</li>`;
                  } else {
                    if (inList) { html += '</ul>'; inList = false; }
                    html += `<p>${escaped}</p>`;
                  }
                }
                if (inList) html += '</ul>';
                return html;
              };

              for (const section of parsedContent.sections) {
                const sectionHtmlContent = buildContentHtml(section.content);

                if (section.type && section.type !== 'text' && validSectionTypes.includes(section.type)) {
                  const sectionId = crypto.randomUUID();
                  const sectionTitle = section.type.split('-').map((word: string) =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

                  const sectionHtml = `<div class="sermon-section sermon-section-${section.type}" data-type="${section.type}" data-section-id="${sectionId}"><h3>${sectionTitle}</h3>${sectionHtmlContent}</div>`;
                  contentParts.push(sectionHtml);
                  contentParts.push('<p></p>');
                } else {
                  contentParts.push(sectionHtmlContent);
                }
              }

              if (contentParts.length > 0) {
                editor.commands.setContent(contentParts.join(''));
                setTimeout(() => {
                  triggerAutosave();
                }, 1000);
              }

              setSaveNotification({
                show: true,
                message: `Successfully imported ${file.name} with ${parsedContent.sections.length} sections`,
                type: 'success'
              });

            } catch (aiError) {
              const errorMsg = aiError instanceof Error ? aiError.message : 'Unknown error';
              const errorStack = aiError instanceof Error ? aiError.stack : '';
              console.error('AI parsing error, falling back to pattern matching');
              console.error('Error message:', errorMsg);
              console.error('Error stack:', errorStack);
              console.error('Full error object:', JSON.stringify(aiError, Object.getOwnPropertyNames(aiError)));
              // Fall back to pattern-based parsing
              parseContentIntoSections(extractedText);
              setSaveNotification({
                show: true,
                message: `Imported ${file.name} (using fallback parser)`,
                type: 'success'
              });
            }
          } else {
            throw new Error('Unsupported file format. Please use .docx or .doc files.');
          }
        } catch (err) {
          console.error('Import error:', err);
          const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred during import';
          setImportError(errorMessage);
          setSaveNotification({
            show: true,
            message: `Failed to import ${file.name}: ${errorMessage}`,
            type: 'error'
          });
        } finally {
          setImportingFile(false);
        }
      }
    });
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }, [editor, parseContentIntoSections]);

  // Handle navigation back
  const handleNavigateBack = useCallback(async () => {
    await saveBeforeExit();
    navigate('/dashboard');
  }, [saveBeforeExit, navigate]);

  // Toggle metadata collapse
  const toggleMetadataCollapse = useCallback(() => setIsMetadataCollapsed(prev => !prev), []);

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(prev => !prev), []);

  // Toggle metadata panel
  const toggleMetadataPanel = useCallback(() => setShowMetadataPanel(prev => !prev), []);

  const handleFormatText = (format: 'bold' | 'italic' | 'underline') => {
    if (!editor) return;

    switch (format) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        editor.chain().focus().toggleUnderline().run();
        break;
    }
    setVisible(false);
  };

  // Handle read commentary — opens the library panel
  const handleReadCommentary = useCallback(() => {
    setShowCommentaryLibrary(true);
    setShowSidebar(false);

    if (!selectedText) return;

    // Try to extract a scripture reference from the selected text
    const referenceMatch = selectedText.match(/([1-3]?\s*[a-zA-Z]+)\s*(\d+)(?::(\d+)(?:-(\d+))?)?/);

    if (referenceMatch) {
      const [fullMatch] = referenceMatch;
      setCommentaryReference(fullMatch);
      setShowCommentary(true);
    } else {
      // If no reference is found, show a notification
      setSaveNotification({
        show: true,
        message: 'No scripture reference found in selected text',
        type: 'error'
      });
    }
  }, [selectedText]);

  // Remove read commentary button
  const [visible, setVisible] = useState(false);

  return (
    <div className={`h-screen flex flex-col ${clarityMode ? 'fixed inset-0 z-[9999] bg-white' : 'bg-sand/10'}`}>
      {/* Editor permission banner — shown when a team member (not owner) is editing */}
      {isEditor && (
        <div className="bg-electric/10 border-b border-electric/20 px-4 py-2 flex items-center justify-between text-sm flex-shrink-0">
          <span className="text-electric font-medium">Editing as Collaborator — your changes save to the original sermon</span>
        </div>
      )}
      {isViewer && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between text-sm flex-shrink-0">
          <span className="text-amber-700 font-medium">View Only — you don't have edit permission for this sermon</span>
        </div>
      )}
      {clarityMode && (
        <div className="border-b border-sand/20 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 flex-1">
            <PenLine className="h-5 w-5 text-electric flex-shrink-0" />
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sermon Title"
              className="text-xl font-bold text-midnight bg-transparent focus:outline-none focus:ring-2 focus:ring-electric rounded px-2 py-1 flex-1 max-w-lg"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActivePanelTab(activePanelTab ? null : 'tools')}
              className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-colors ${activePanelTab ? 'bg-electric/10 border-electric/40 text-electric' : 'bg-sand/10 hover:bg-sand/20 border-sand/30 text-slate hover:text-midnight'}`}
            >
              <PanelRight className="h-4 w-4" />
              <span className="hidden sm:inline">Panel</span>
              {activePanelTab && <span className="w-1.5 h-1.5 rounded-full bg-electric inline-block ml-0.5" />}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-3 py-1.5 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 text-sm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Save</span>
            </button>
            <button
              onClick={() => setClarityMode(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate hover:text-midnight hover:bg-sand/20 rounded-lg transition-colors text-sm"
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Exit Clarity Mode</span>
            </button>
          </div>
        </div>
      )}
      {/* Top Bar */}
      <div className={`bg-white border-b border-sand/20 p-4 ${clarityMode ? 'hidden' : ''}`}>
        <div className="responsive-container">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button onClick={handleNavigateBack} className="mr-4 text-slate hover:text-midnight">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sermon Title"
                className="text-xl md:text-2xl font-bold text-midnight bg-transparent px-2 md:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-electric w-full"
              />
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              <button
                onClick={toggleMobileMenu}
                className="md:hidden flex items-center p-2 bg-sand/10 hover:bg-sand/20 rounded-lg text-slate"
                aria-label="Toggle mobile menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="hidden md:flex items-center space-x-3">
                {/* Normal / Clarity pill toggle */}
                <div className="flex items-center bg-sand/10 border border-sand/30 rounded-lg p-0.5">
                  <button
                    onClick={() => setClarityMode(false)}
                    className="px-3 py-1.5 rounded-md text-sm bg-white shadow-sm text-midnight font-medium"
                  >
                    Normal
                  </button>
                  <button
                    onClick={() => setClarityMode(true)}
                    className="px-3 py-1.5 rounded-md text-sm text-slate hover:text-electric transition-colors"
                  >
                    Clarity
                  </button>
                </div>
                {/* Preach Mode */}
                <button
                  onClick={handlePreachMode}
                  title="Preach Mode"
                  aria-label="Preach Mode"
                  className="flex items-center gap-1.5 px-3 py-2 text-emerald hover:bg-emerald/10 border border-emerald/30 rounded-lg transition-colors text-sm"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden lg:inline">Preach</span>
                </button>
                {/* Unified panel toggle */}
                <button
                  onClick={() => setActivePanelTab(activePanelTab ? null : 'tools')}
                  className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${activePanelTab ? 'bg-electric/10 border-electric/40 text-electric' : 'bg-sand/10 hover:bg-sand/20 border-sand/30 text-slate hover:text-midnight'}`}
                >
                  <PanelRight className="h-4 w-4" />
                  <span className="hidden lg:inline">Panel</span>
                  {activePanelTab && <span className="w-1.5 h-1.5 rounded-full bg-electric inline-block ml-0.5" />}
                </button>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-3 md:px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 md:mr-2 animate-spin" /> : <Save className="h-4 w-4 md:mr-2" />}
                <span className="hidden md:inline">Save</span>
              </button>
            </div>
          </div>
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white p-4 rounded-lg shadow-lg border border-sand/20 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setClarityMode(true)}
                  className="flex items-center justify-center p-3 bg-electric/10 hover:bg-electric/20 rounded-lg text-electric"
                >
                  <div className="flex flex-col items-center">
                    <PenLine className="h-5 w-5 mb-1" />
                    <span className="text-xs">Clarity</span>
                  </div>
                </button>
                <button
                  onClick={handlePreachMode}
                  className="flex items-center justify-center p-3 bg-emerald/10 hover:bg-emerald/20 rounded-lg text-emerald"
                >
                  <div className="flex flex-col items-center">
                    <Eye className="h-5 w-5 mb-1" />
                    <span className="text-xs">Preach</span>
                  </div>
                </button>
                <button
                  onClick={() => setActivePanelTab(activePanelTab ? null : 'tools')}
                  className={`flex items-center justify-center p-3 rounded-lg ${activePanelTab ? 'bg-electric/10 text-electric' : 'bg-sand/10 hover:bg-sand/20 text-slate'}`}
                >
                  <div className="flex flex-col items-center">
                    <PanelRight className="h-5 w-5 mb-1" />
                    <span className="text-xs">Panel</span>
                  </div>
                </button>
                <button
                  onClick={() => setActivePanelTab(activePanelTab === 'commentary' ? null : 'commentary')}
                  className={`flex items-center justify-center p-3 rounded-lg ${activePanelTab === 'commentary' ? 'bg-gold/20 text-gold' : 'bg-gold/10 hover:bg-gold/20 text-gold'}`}
                >
                  <div className="flex flex-col items-center">
                    <BookOpen className="h-5 w-5 mb-1" />
                    <span className="text-xs">Commentary</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Metadata Panel Button */}
          <div className={`flex justify-between items-center mb-2 ${clarityMode ? 'hidden' : ''}`}>
            <button
              onClick={toggleMetadataPanel}
              className="flex items-center text-slate hover:text-midnight"
            >
              <span className="mr-2">Sermon Details</span>
              {showMetadataPanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Metadata Panel */}
          {showMetadataPanel && !clarityMode && (
            <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-1">Scripture</label>
                  <input
                    type="text"
                    value={scripture}
                    onChange={(e) => setScripture(e.target.value)}
                    placeholder="e.g., John 3:16-17"
                    className="w-full bg-transparent border border-electric/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-electric text-midnight"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate mb-1">Date</label>
                  <input
                    type="date"
                    value={date || ''}
                    onChange={(e) => setDate(e.target.value || null)}
                    className="w-full bg-transparent border border-electric/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-electric"
                  />
                </div>
                {isOwner && <div className="relative" ref={seriesDropdownRef}>
                  <label className="block text-sm font-medium text-slate mb-1">Series</label>
                  <button
                    onClick={() => setShowSeriesDropdown(!showSeriesDropdown)}
                    className="flex items-center justify-between w-full px-3 py-2 bg-transparent border border-electric/30 rounded-lg text-sm text-slate"
                    aria-expanded={showSeriesDropdown}
                  >
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-2" />
                      <span>{seriesId ? series.find(s => s.id === seriesId)?.title || 'Select Series' : 'Select Series'}</span>
                    </div>
                    {showSeriesDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {showSeriesDropdown && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-sand/20 py-1 z-50">
                      <button
                        onClick={() => { setSeriesId(null); setShowSeriesDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-sand/10"
                      >
                        None
                      </button>
                      {series.map(s => (
                        <button
                          key={s.id}
                          onClick={() => { setSeriesId(s.id); setShowSeriesDropdown(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-sand/10"
                        >
                          {s.title}
                        </button>
                      ))}
                      <div className="border-t border-sand/20 mt-1 pt-1">
                        <button
                          onClick={handleCreateSeries}
                          className="w-full px-4 py-2 text-left text-sm text-electric hover:bg-sand/10"
                        >
                          + Create New Series
                        </button>
                      </div>
                    </div>
                  )}
                </div>}
                <div className="relative" ref={statusDropdownRef}>
                  <label className="block text-sm font-medium text-slate mb-1">Status</label>
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className="flex items-center justify-between w-full px-3 py-2 bg-transparent border border-electric/30 rounded-lg text-sm"
                    aria-expanded={showStatusDropdown}
                  >
                    <div className="flex items-center">
                      {status === 'draft' ? (
                        <Clock className="h-4 w-4 mr-2 text-gold" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2 text-emerald" />
                      )}
                      <span className="capitalize">{status}</span>
                    </div>
                    {showStatusDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {showStatusDropdown && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-sand/20 py-1 z-50">
                      <button
                        onClick={() => { setStatus('draft'); setShowStatusDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-sand/10 flex items-center"
                      >
                        <Clock className="h-4 w-4 mr-2 text-gold" />
                        Draft
                      </button>
                      <button
                        onClick={() => { setStatus('complete'); setShowStatusDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-sand/10 flex items-center"
                      >
                        <CheckCircle className="h-4 w-4 mr-2 text-emerald" />
                        Complete
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate mb-1">YouTube URL (optional)</label>
                <input
                  type="text"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="e.g., https://www.youtube.com/watch?v=..."
                  className="w-full bg-transparent border border-electric/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-electric text-midnight"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {editor && (
        <div className={`${isMobileView && !clarityMode ? 'sticky top-0 z-[var(--z-sticky)]' : ''} bg-white border-b border-sand/20 flex-shrink-0`}>
          <FormatBar editor={editor} isMobileView={clarityMode ? false : isMobileView} />
        </div>
      )}
      <div className={`flex-1 overflow-hidden bg-white flex ${clarityMode ? 'min-h-0' : ''}`}>
        <div className="flex-1 overflow-auto relative min-w-0">
          <div className={`mx-auto pb-16 ${clarityMode ? 'max-w-5xl px-10 pt-10 pb-64 md:px-16 md:pt-16 md:pb-80' : 'max-w-4xl p-4 md:p-8 md:pb-24'}`} ref={editorRef}>
            <EditorContent editor={editor} className="prose prose-lg max-w-none" />
            {editor && selectedText && (
              <TextSelectionMenu
                selectedText={selectedText}
                onEnhanceText={() => setShowTextEnhancer(true)}
                editorRef={editorRef}
                editor={editor}
              />
            )}
          </div>
        </div>
        {/* Panel spacer — pushes editor content left when panel is open */}
        <div
          aria-hidden="true"
          className={`hidden md:block flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${activePanelTab ? 'w-[420px]' : 'w-0'}`}
        />

        {/* Unified Side Panel */}
        <div
          className={`fixed right-0 top-0 bottom-0 z-[var(--z-sidebar)] w-full md:w-[420px] bg-white dark:bg-charcoal border-l border-sand/20 dark:border-slate/20 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${activePanelTab ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {/* Tab bar */}
          <div className="flex-shrink-0 flex items-center border-b border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal">
            {([
              { id: 'guide' as const, label: 'Guide', icon: Compass },
              { id: 'commentary' as const, label: 'Commentary', icon: BookOpen },
              { id: 'tools' as const, label: 'Tools', icon: Wrench },
              { id: 'generate' as const, label: 'Generate', icon: Sparkles },
              { id: 'assistant' as const, label: 'Assistant', icon: Book },
            ]).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActivePanelTab(id)}
                title={label}
                className={`flex items-center gap-1.5 px-3 py-3.5 text-xs font-medium border-b-2 transition-all duration-150 whitespace-nowrap ${
                  activePanelTab === id
                    ? 'border-electric text-midnight dark:text-soft-white'
                    : 'border-transparent text-slate dark:text-soft-white/50 hover:text-midnight dark:hover:text-soft-white'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {activePanelTab === id && <span>{label}</span>}
              </button>
            ))}
            <div className="flex-1" />
            <button
              onClick={() => setActivePanelTab(null)}
              className="p-2 mr-2 text-slate hover:text-midnight dark:text-soft-white/50 dark:hover:text-soft-white rounded-lg hover:bg-sand/20 dark:hover:bg-slate/20 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Panel content */}
          <div className={`flex-1 min-h-0 ${activePanelTab === 'assistant' ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'}`}>

            {/* ── Assistant Tab (always mounted to preserve history) ── */}
            <div className={activePanelTab === 'assistant' ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
              <TheologicalAssistant mode="panel" sermonId={id} />
            </div>

            {/* ── Passage Guide Tab ── */}
            {activePanelTab === 'guide' && (
              <PassageGuide mode="panel" defaultSermonId={id} sermonScripture={scripture} />
            )}

            {/* ── Commentary Tab ── */}
            {activePanelTab === 'commentary' && (
              <CommentaryLibrary
                embedded
                isOpen={true}
                onClose={() => setActivePanelTab(null)}
                sermonScripture={scripture}
                onInsertQuote={(text, sourceName, reference) => {
                  if (!editor) return;
                  const attribution = `${sourceName} on ${reference}`;
                  editor.chain().focus().command(({ tr, state, dispatch }) => {
                    const { from } = state.selection;
                    const schema = state.schema;
                    const paragraph = schema.nodes.paragraph.create({}, schema.text(text));
                    const sectionNode = schema.nodes.sermonSection.create(
                      { type: 'quote', attribution, id: crypto.randomUUID() },
                      paragraph
                    );
                    if (dispatch) tr.insert(from, sectionNode);
                    return true;
                  }).run();
                }}
              />
            )}

            {/* ── Tools Tab ── */}
            {activePanelTab === 'tools' && (
              <div className="p-4 space-y-4">
                {/* 2-column action card grid */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleImportWord}
                    className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate/10 dark:bg-slate/20 flex items-center justify-center">
                      <FileUp className="h-5 w-5 text-slate" />
                    </div>
                    <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Import Word</span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Bring in a .docx sermon file</span>
                  </button>

                  <button
                    onClick={handleAnalyzeSermon}
                    disabled={analyzing}
                    className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="w-10 h-10 rounded-xl bg-electric/10 dark:bg-electric/15 flex items-center justify-center">
                      {analyzing ? <Loader2 className="h-5 w-5 text-electric animate-spin" /> : <Brain className="h-5 w-5 text-electric" />}
                    </div>
                    <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Analyze Sermon</span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">AI review of structure & themes</span>
                  </button>

                  <button
                    onClick={handleLanguageAnalysis}
                    disabled={langAnalyzing}
                    className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 dark:bg-amber-500/15 flex items-center justify-center">
                      {langAnalyzing ? <Loader2 className="h-5 w-5 text-amber-600 animate-spin" /> : <Languages className="h-5 w-5 text-amber-600" />}
                    </div>
                    <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Language Analysis</span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Hebrew & Greek word study</span>
                  </button>

                  <button
                    onClick={() => setShowTextEnhancer(true)}
                    disabled={!selectedText}
                    className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald/10 dark:bg-emerald/15 flex items-center justify-center">
                      <Target className="h-5 w-5 text-emerald" />
                    </div>
                    <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Enhance Text</span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Select a portion of text first</span>
                  </button>

                  <button
                    onClick={() => setShowSlideEditor(true)}
                    className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square"
                  >
                    <div className="w-10 h-10 rounded-xl bg-electric/10 dark:bg-electric/15 flex items-center justify-center">
                      <Presentation className="h-5 w-5 text-electric" />
                    </div>
                    <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Create Slides</span>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Tap slide icon in a sermon section</span>
                  </button>

                  {isOwner && (
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square"
                    >
                      <div className="w-10 h-10 rounded-xl bg-electric/10 dark:bg-electric/15 flex items-center justify-center">
                        <Share2 className="h-5 w-5 text-electric" />
                      </div>
                      <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Share Sermon</span>
                      <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Invite collaborators to view</span>
                    </button>
                  )}

                  <div className="relative">
                    <button
                      onClick={() => setShowExportDropdown(v => !v)}
                      className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square w-full"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gold/10 dark:bg-gold/15 flex items-center justify-center">
                        <Download className="h-5 w-5 text-gold" />
                      </div>
                      <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">Export</span>
                      <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">Download sermon file</span>
                    </button>
                    {showExportDropdown && (
                      <>
                        <div className="fixed inset-0 z-[1]" onClick={() => setShowExportDropdown(false)} />
                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-charcoal border border-sand/20 dark:border-slate/20 rounded-xl shadow-lg overflow-hidden z-[2]">
                          <button onClick={() => { handleExportForSermonScribe(); setShowExportDropdown(false); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-midnight dark:text-soft-white hover:bg-sand/10 dark:hover:bg-slate/10 transition-colors text-left">
                            <FileText className="h-3.5 w-3.5 text-slate flex-shrink-0" />
                            SermonScribe (.txt)
                          </button>
                          <button onClick={() => { handleExportPDF(); setShowExportDropdown(false); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-midnight dark:text-soft-white hover:bg-sand/10 dark:hover:bg-slate/10 transition-colors border-t border-sand/10 dark:border-slate/10 text-left">
                            <Download className="h-3.5 w-3.5 text-crimson flex-shrink-0" />
                            Export PDF
                          </button>
                          <button onClick={() => { handleExportWord(); setShowExportDropdown(false); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-midnight dark:text-soft-white hover:bg-sand/10 dark:hover:bg-slate/10 transition-colors border-t border-sand/10 dark:border-slate/10 text-left">
                            <FileText className="h-3.5 w-3.5 text-electric flex-shrink-0" />
                            Export Word (.docx)
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Saved Graphics */}
                {savedGraphics.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-slate dark:text-soft-white/40 mb-2">Saved Graphics</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {savedGraphics.map((graphic) => {
                        const url = getMediaPublicUrl(graphic.file_path);
                        return (
                          <div key={graphic.id} className="relative group rounded-xl overflow-hidden aspect-square bg-sand/10">
                            <img src={url} alt="Saved graphic" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex gap-1">
                                <a href={url} download className="p-1.5 bg-white/90 rounded-full hover:bg-white" title="Download">
                                  <Download className="h-3.5 w-3.5 text-midnight" />
                                </a>
                                <button onClick={() => deleteMediaFile(graphic.id)} className="p-1.5 bg-white/90 rounded-full hover:bg-white" title="Delete" type="button">
                                  <Trash2 className="h-3.5 w-3.5 text-crimson" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Analysis result — inline instead of navigating to Research */}
                {analysisResult && (
                  <div className="rounded-xl border border-electric/20 bg-electric/5 dark:bg-electric/10 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-4 w-4 text-electric" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-electric">Analysis</span>
                    </div>
                    <p className="text-xs text-midnight dark:text-soft-white leading-relaxed whitespace-pre-wrap line-clamp-[12]">{analysisResult}</p>
                    <button
                      onClick={() => navigate('/dashboard/research', { state: { analysis: fullAnalysisResult, sermonId: id, sermonTitle: title } })}
                      className="mt-2 block text-xs text-electric hover:underline text-left"
                    >
                      View full analysis →
                    </button>
                  </div>
                )}

                {/* Language analysis term cards */}
                {fullLangResult && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
                      <div className="flex items-center gap-2">
                        <Languages className="h-4 w-4 text-amber-600" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">Language Analysis</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleSaveLangResult}
                          disabled={langSaved}
                          className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg transition-colors ${langSaved ? 'text-emerald bg-emerald/10' : 'text-amber-700 bg-amber-100 hover:bg-amber-200'}`}
                          title={langSaved ? 'Saved' : 'Save word study'}
                        >
                          {langSaved ? <><Check className="h-3 w-3" />Saved</> : <><Save className="h-3 w-3" />Save</>}
                        </button>
                        <button
                          onClick={() => navigate('/dashboard/research', { state: { languageAnalysis: fullLangResult, sermonId: id, sermonTitle: title, tool: 'language' } })}
                          className="text-[10px] text-amber-600 hover:text-amber-800 hover:underline"
                        >
                          Full word study →
                        </button>
                      </div>
                    </div>
                    {fullLangResult.exegeticalSummary && (
                      <div className="px-3 py-2.5 bg-amber-100/60 border-b border-amber-200">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">Exegetical Summary</p>
                        <p className="text-xs text-midnight leading-relaxed">{fullLangResult.exegeticalSummary}</p>
                      </div>
                    )}
                    <div className="divide-y divide-amber-100">
                      {fullLangResult.terms.map((term, i) => (
                        <div key={i} className="p-3">
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div>
                              <span className="text-base font-serif leading-none">{term.word}</span>
                              <span className="text-[10px] text-slate ml-1.5 font-mono">{term.transliteration}</span>
                              <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full font-medium">{term.strongsNumber}</span>
                            </div>
                            <button
                              onClick={() => insertLanguageTerm(term, i)}
                              className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                                langInsertedId === i
                                  ? 'bg-emerald/20 text-emerald'
                                  : 'bg-amber-600 text-white hover:bg-amber-700'
                              }`}
                            >
                              {langInsertedId === i ? <><Check className="h-3 w-3" />Inserted</> : <><PenLine className="h-3 w-3" />Insert</>}
                            </button>
                          </div>
                          <p className="text-[10px] text-slate italic mb-1">{term.literalMeaning}</p>
                          {term.scriptureReferences && (
                            <p className="text-[10px] text-amber-700 font-mono mb-1">{term.scriptureReferences}</p>
                          )}
                          <p className="text-xs text-midnight leading-relaxed">{term.preachingApplication}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status / errors */}
                {analysisError && (
                  <div className="p-4 bg-crimson/10 text-crimson rounded-xl flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Analysis Error</p>
                      <p className="text-xs mt-0.5">{analysisError}</p>
                    </div>
                  </div>
                )}
                {langError && (
                  <div className="p-4 bg-amber-50 text-amber-800 rounded-xl flex items-start gap-2 border border-amber-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Language Analysis Error</p>
                      <p className="text-xs mt-0.5">{langError}</p>
                    </div>
                  </div>
                )}
                {importError && (
                  <div className="p-4 bg-crimson/10 text-crimson rounded-xl flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Import Error</p>
                      <p className="text-xs mt-0.5">{importError}</p>
                    </div>
                  </div>
                )}
                {importingFile && (
                  <div className="p-4 bg-electric/10 text-electric rounded-xl flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Processing File</p>
                      <p className="text-xs mt-0.5">This may take a moment...</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Generate Tab ── */}
            {activePanelTab === 'generate' && (
              <div className="p-4">
                <p className="text-xs text-slate dark:text-soft-white/50 mb-4">Opens on the Tools page with this sermon pre-selected.</p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { toolId: 'image-generator',  icon: ImageIcon,    badge: 'bg-gold/10 dark:bg-gold/15',         iconCls: 'text-gold',     label: 'Image Generator',   desc: 'Custom sermon graphic'   },
                    { toolId: 'study-questions',   icon: FileText,     badge: 'bg-electric/10 dark:bg-electric/15', iconCls: 'text-electric', label: 'Study Questions',   desc: 'Small group discussion'  },
                    { toolId: 'family-devotional', icon: Heart,        badge: 'bg-gold/10 dark:bg-gold/15',         iconCls: 'text-gold',     label: 'Family Devotional', desc: 'Take-home family guide'  },
                    { toolId: 'service-assets',    icon: Music,        badge: 'bg-electric/10 dark:bg-electric/15', iconCls: 'text-electric', label: 'Service Assets',    desc: 'Songs & scripture picks' },
                    { toolId: 'summarizer',        icon: FileText,     badge: 'bg-gold/10 dark:bg-gold/15',         iconCls: 'text-gold',     label: 'Study Guide',       desc: 'Shareable study page'    },
                    { toolId: 'translator',        icon: Languages,    badge: 'bg-slate/10 dark:bg-slate/20',       iconCls: 'text-slate',    label: 'Translate',         desc: 'Multi-language sermon'   },
                    { toolId: 'social-media',      icon: Share2,       badge: 'bg-electric/10 dark:bg-electric/15', iconCls: 'text-electric', label: 'Social Media',      desc: 'Posts & graphics'        },
                  ] as const).map(({ toolId, icon: Icon, badge, iconCls, label, desc }) => (
                    <button
                      key={toolId}
                      onClick={async () => {
                        await saveBeforeExit();
                        navigate('/dashboard/tools', { state: { toolId, sermonId: id } });
                      }}
                      className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal/50 hover:border-electric/30 hover:bg-sand/10 dark:hover:bg-slate/10 transition-all duration-150 aspect-square"
                    >
                      <div className={`w-10 h-10 rounded-xl ${badge} flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${iconCls}`} />
                      </div>
                      <span className="text-xs font-medium text-midnight dark:text-soft-white text-center leading-tight">{label}</span>
                      <span className="text-[10px] text-slate/60 dark:text-soft-white/30 text-center leading-tight">{desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      <SaveNotification
        show={saveNotification.show}
        onHide={() => setSaveNotification({ ...saveNotification, show: false })}
        message={saveNotification.message}
        type={saveNotification.type}
      />
      {showTextEnhancer && (
        <TextEnhancer
          selectedText={selectedText}
          onClose={() => setShowTextEnhancer(false)}
          onApply={handleApplyEnhancement}
        />
      )}
      {showShareModal && (
        <SermonShareModal
          sermonId={id || ''}
          sermonTitle={title}
          sermonScripture={scripture}
          sermonContent={editor?.getHTML() || ''}
          youtubeUrl={youtubeUrl}
          onClose={() => setShowShareModal(false)}
        />
      )}
      <SlideEditor
        isOpen={showSlideEditor}
        sermonContent={editor?.getHTML() || ''}
        sermonTitle={title}
        onClose={() => setShowSlideEditor(false)}
      />
      {showCreateSeriesModal && (
        <div className="modal-container">
          <div className="modal-content p-6">
            <h3 className="text-lg font-semibold text-midnight mb-4">Create New Series</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-1">Series Title</label>
                <input
                  type="text"
                  value={newSeriesName}
                  onChange={(e) => setNewSeriesName(e.target.value)}
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  placeholder="Enter series title"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-1">Description (Optional)</label>
                <textarea
                  value={newSeriesDescription}
                  onChange={(e) => setNewSeriesDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  placeholder="Enter series description"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateSeriesModal(false);
                    setNewSeriesName('');
                    setNewSeriesDescription('');
                  }}
                  className="px-4 py-2 text-slate hover:text-midnight"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSeriesSubmit}
                  disabled={!newSeriesName.trim()}
                  className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
                >
                  Create Series
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCommentary && (
        <CommentaryViewer
          passage={commentaryReference}
          onClose={() => setShowCommentary(false)}
        />
      )}
      <div className="md:hidden fixed bottom-20 right-4 flex flex-col space-y-2 z-[var(--z-dropdown)]">
        {!activePanelTab && (
          <button
            onClick={() => setActivePanelTab('tools')}
            className="bg-electric text-white p-3 rounded-full shadow-lg hover:bg-electric/90"
          >
            <PanelRight className="h-5 w-5" />
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald text-white p-3 rounded-full shadow-lg hover:bg-emerald/90 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}